import { useApiFetch } from '~/composables/useApiFetch';
import type {
  App,
  AppListResponse,
  ConfigsResponse,
  CreateAppRequest,
  ToggleAppChatUiActiveRequest,
  UpdateAppRequest,
} from '~/types/app';
import { unwrapEnvelope, unwrapListEnvelope } from '~/utils/apiEnvelope';

const DEFAULT_APP_LIST_PAGE_SIZE = 12;

export interface GetAppListApiParams {
  page_on?: number;
  page_size?: number;
}

export async function getAppListApi(params?: GetAppListApiParams): Promise<AppListResponse> {
  const page_on = params?.page_on ?? 1;
  const page_size = params?.page_size ?? DEFAULT_APP_LIST_PAGE_SIZE;
  const query = new URLSearchParams({
    page_on: String(page_on),
    page_size: String(page_size),
  });
  const response = await useApiFetch(`/app/list?${query.toString()}`, {
    method: 'GET',
    requireAuth: true,
  });
  const body = await response.json();
  const u = unwrapListEnvelope<App>(body);
  return {
    list: u.list,
    page_on: u.page_on,
    page_size: u.page_size,
    total: u.total,
    keyword: '',
    not_group_total: 0,
    sort: '',
    organizations_uuid: '',
  } as AppListResponse;
}

export async function getAppApi(appId: number): Promise<App> {
  const response = await useApiFetch(`/app/?id=${appId}`, {
    method: 'GET',
    requireAuth: true,
  });
  return unwrapEnvelope<App>(await response.json());
}

/** Quick Create returns a UUID; the existing application detail route uses a numeric ID. */
export async function resolveAppIdApi(applicationUuid: string): Promise<number> {
  let page = 1;
  let seen = 0;
  while (true) {
    const result = await getAppListApi({ page_on: page, page_size: 100 });
    const match = result.list.find((app) => app.application_uuid === applicationUuid);
    if (match && Number.isSafeInteger(match.id) && match.id > 0) return match.id;
    seen += result.list.length;
    if (!result.list.length || seen >= result.total) break;
    page += 1;
  }
  throw new Error('Application UUID was not found in the accessible application list');
}

export async function deleteAppApi(appId: number): Promise<boolean> {
  const response = await useApiFetch(`/app/?id=${appId}`, {
    method: 'DELETE',
    requireAuth: true,
  });
  return unwrapEnvelope<boolean>(await response.json());
}

export async function createAppApi(appRequest: CreateAppRequest): Promise<number> {
  const response = await useApiFetch('/app', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify(appRequest),
  });
  return unwrapEnvelope<number>(await response.json());
}

export async function updateAppApi(appRequest: UpdateAppRequest): Promise<App> {
  const response = await useApiFetch('/app', {
    method: 'PUT',
    requireAuth: true,
    body: JSON.stringify(appRequest),
  });
  return unwrapEnvelope<App>(await response.json());
}

export async function toggleAppChatUiActiveApi(
  appRequest: ToggleAppChatUiActiveRequest
): Promise<App> {
  const response = await useApiFetch('/app/enable_chat_ui', {
    method: 'PUT',
    requireAuth: true,
    body: JSON.stringify(appRequest),
  });
  return unwrapEnvelope<App>(await response.json());
}

/**
 * 獲取系統配置
 * @returns 系統配置資訊，包含 is_open_invite 等設定
 */
export async function getConfigsApi(): Promise<ConfigsResponse> {
  const response = await useApiFetch('/configs/', {
    method: 'GET',
    requireAuth: false, // 配置 API 不需要認證，註冊流程中可調用
  });
  return unwrapEnvelope<ConfigsResponse>(await response.json());
}
